import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { indianStates } from '../Components/states';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const EmployeeEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'basic');
  const [validatedTabs, setValidatedTabs] = useState(['basic']); // Track which tabs have been validated
  const [originalStatus, setOriginalStatus] = useState(''); // Track original employee status
  const [formData, setFormData] = useState({
    // Basic Info
    firstName: '',
    middleName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    bloodGroup: '',
    citizenship: '',
    email: '',
    mobileNumber: '',
    countryCode: '+91',
    maritalStatus: '',
    aadharNumber: '',
    panNumber: '',
    // Address
    addresses: [],
    // Employment
    employeeId: '',
    employmentType: '',
    designation: '',
    department: '',
    reportingManager: '',
    pfUanNumber: '',
    pfNumber: '',
    esiNumber: '',
    dateOfJoining: '',
    employmentStatus: '',
    pfApplicable: false,
    tdsApplicable: false,
    remoteWorkAllowed: false,
    checkInCheckOutNotRequired: false,
    // Experience
    experiences: [],
    // Education
    educationDetails: [],
    // Bank
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    branchName: '',
    accountHolderName: '',
    // Document
    aadharCard: null,
    panCard: null,
    educationCertificates: []
  });
  const [errors, setErrors] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({
    aadharCard: false,
    panCard: false,
    educationCertificates: {}
  });
  const [designations, setDesignations] = useState([]);
  const [loadingDesignations, setLoadingDesignations] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [reportingManagers, setReportingManagers] = useState([]);
  const [loadingReportingManagers, setLoadingReportingManagers] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressData, setAddressData] = useState({
    addressType: '',
    address1: '',
    address2: '',
    country: 'India',
    state: 'Tamil Nadu',
    city: '',
    pincode: '',
    sameAsPermanent: false,
    editingAddressId: null // Track which address is being edited
  });
  const [addressErrors, setAddressErrors] = useState({});
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const stateDropdownRef = useRef(null);
  const [showAddressTypeDropdown, setShowAddressTypeDropdown] = useState(false);
  const [addressTypeSearchTerm, setAddressTypeSearchTerm] = useState('');
  const addressTypeDropdownRef = useRef(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const countryDropdownRef = useRef(null);

  const addressTypes = ['Permanent Address', 'Present Address'];
  const countries = ['India', 'USA', 'UK'];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setShowStateDropdown(false);
        setStateSearchTerm('');
      }
      if (addressTypeDropdownRef.current && !addressTypeDropdownRef.current.contains(event.target)) {
        setShowAddressTypeDropdown(false);
        setAddressTypeSearchTerm('');
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
        setCountrySearchTerm('');
      }
    };

    if (showStateDropdown || showAddressTypeDropdown || showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStateDropdown, showAddressTypeDropdown, showCountryDropdown]);

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'employment', label: 'Employment' },
    { id: 'experience', label: 'Experience' },
    { id: 'education', label: 'Education' },
    { id: 'bank', label: 'Bank' },
    { id: 'document', label: 'Document' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    let validatedValue = value;

    // Validate Aadhar Number - only numeric, no alphabets, max 12 digits
    if (name === 'aadharNumber') {
      validatedValue = value.replace(/\D/g, '').slice(0, 12);
    }

    // Validate Account Number - only numeric, no alphabets
    if (name === 'accountNumber') {
      validatedValue = value.replace(/\D/g, '');
    }

    // Validate Bank Name - only alphabets and spaces, no numeric
    if (name === 'bankName') {
      validatedValue = value.replace(/[^a-zA-Z\s]/g, '');
    }

    // Validate Account Holder Name - only alphabets and spaces, no numeric
    if (name === 'accountHolderName') {
      validatedValue = value.replace(/[^a-zA-Z\s]/g, '');
    }

    setFormData(prev => ({
      ...prev,
      [name]: validatedValue
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleMobileNumberChange = (e) => {
    const value = e.target.value;
    // Only allow numeric characters and limit to 10 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({
      ...prev,
      mobileNumber: numericValue
    }));
    // Clear error when user starts typing
    if (errors.mobileNumber) {
      setErrors(prev => ({
        ...prev,
        mobileNumber: ''
      }));
    }
  };

  // Helper function to format date from YYYY-MM-DD to DD-MM-YYYY
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    // If already in DD-MM-YYYY format, return as is
    if (dateString.includes('-') && dateString.split('-')[0].length === 2) {
      return dateString;
    }
    // Convert from YYYY-MM-DD to DD-MM-YYYY
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper function to convert API date (ISO) to form format (YYYY-MM-DD)
  const formatDateToYYYYMMDD = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return '';
    }
  };

  // Handle tab from URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['basic', 'employment', 'experience', 'education', 'bank', 'document'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch designations, departments, and reporting managers when employment tab is accessed
  useEffect(() => {
    if (activeTab === 'employment') {
      if (designations.length === 0 && !loadingDesignations) {
        fetchDesignations();
      }
      if (departments.length === 0 && !loadingDepartments) {
        fetchDepartments();
      }
      if (reportingManagers.length === 0 && !loadingReportingManagers) {
        fetchReportingManagers();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch employee data on component mount
  useEffect(() => {
    const fetchEmployee = async () => {
      if (!id) {
        toast.error('Employee ID is missing');
        navigate('/employee');
        return;
      }

      if (!API_URL) {
        toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const payload = {
          action: 'get-employee',
          id: id
        };

        const response = await axios.post(API_URL, payload, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const result = response.data;

        if (result?.success && result?.employee) {
          const employee = result.employee;

          // Store original status
          const status = employee.employmentStatus || 'active';
          setOriginalStatus(status);

          // Populate formData with employee data
          setFormData({
            firstName: employee.firstName || '',
            middleName: employee.middleName || '',
            lastName: employee.lastName || '',
            gender: employee.gender || '',
            dateOfBirth: formatDateToYYYYMMDD(employee.dateOfBirth) || '',
            bloodGroup: employee.bloodGroup || '',
            citizenship: employee.citizenship || '',
            email: employee.email || '',
            mobileNumber: employee.mobileNumber || '',
            countryCode: employee.countryCode || '+91',
            maritalStatus: employee.maritalStatus || '',
            aadharNumber: employee.aadharNumber || '',
            panNumber: employee.panNumber || '',
            addresses: employee.addresses || [],
            employeeId: employee.employeeId || '',
            employmentType: employee.employmentType || '',
            designation: employee.designation || '',
            department: employee.department || '',
            reportingManager: employee.reportingManager || '',
            pfUanNumber: employee.pfUanNumber || '',
            pfNumber: employee.pfNumber || '',
            esiNumber: employee.esiNumber || '',
            dateOfJoining: formatDateToYYYYMMDD(employee.dateOfJoining) || '',
            employmentStatus: status,
            pfApplicable: employee.pfApplicable || false,
            tdsApplicable: employee.tdsApplicable || false,
            remoteWorkAllowed: employee.remoteWorkAllowed || false,
            checkInCheckOutNotRequired: employee.checkInCheckOutNotRequired || false,
            experiences: (employee.experiences || []).map(exp => ({
              ...exp,
              periodFrom: formatDateToYYYYMMDD(exp.periodFrom) || '',
              periodTo: exp.isPresent ? '' : (formatDateToYYYYMMDD(exp.periodTo) || '')
            })),
            educationDetails: (employee.educationDetails || []).map(edu => ({
              ...edu,
              relevantCertificateNo: edu.relevantCertificateNo || '',
              fieldOfStudy: edu.fieldOfStudy || '',
              nameOfDegree: edu.nameOfDegree || ''
            })),
            bankName: employee.bankName || '',
            accountNumber: employee.accountNumber || '',
            ifscCode: employee.ifscCode || '',
            branchName: employee.branchName || '',
            accountHolderName: employee.accountHolderName || '',
            aadharCard: employee.aadharCard || null,
            panCard: employee.panCard || null,
            educationCertificates: employee.educationCertificates || []
          });

          // Mark all tabs as validated since we're editing existing data
          setValidatedTabs(['basic', 'employment', 'experience', 'education', 'bank', 'document']);
        } else {
          toast.error(result?.message || 'Failed to fetch employee data');
          navigate('/employee');
        }
      } catch (error) {
        console.error('Error fetching employee:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
        toast.error(errorMessage);
        navigate('/employee');
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };

    fetchEmployee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Helper function to prepare addresses for API
  const prepareAddresses = () => {
    const addresses = [];

    formData.addresses.forEach((address) => {
      const addressData = {
        addressType: address.addressType,
        address1: address.address1,
        address2: address.address2 || '',
        country: address.country || 'India',
        state: address.state,
        city: address.city,
        pincode: address.pincode
      };
      addresses.push(addressData);
    });

    return addresses;
  };

  // Helper function to prepare experiences for API
  const prepareExperiences = () => {
    return formData.experiences.map((exp) => ({
      companyName: exp.companyName || '',
      designation: exp.designation || '',
      periodFrom: exp.periodFrom ? formatDateToDDMMYYYY(exp.periodFrom) : '',
      periodTo: exp.isPresent ? '' : (exp.periodTo ? formatDateToDDMMYYYY(exp.periodTo) : ''),
      isPresent: exp.isPresent || false,
      duration: exp.duration || ''
    }));
  };

  // Fetch designations from API
  const fetchDesignations = async () => {
    if (!API_URL) {
      return;
    }

    setLoadingDesignations(true);
    try {
      const payload = {
        action: 'get-designations',
        page: 1,
        limit: 50,
        status: 'active'
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success && result?.data) {
        setDesignations(result.data || []);
      } else {
        console.error('Failed to fetch designations:', result?.message);
      }
    } catch (error) {
      console.error('Error fetching designations:', error);
      // Don't show error toast as this is a background operation
    } finally {
      setLoadingDesignations(false);
    }
  };

  // Fetch departments from API
  const fetchDepartments = async () => {
    if (!API_URL) {
      return;
    }

    setLoadingDepartments(true);
    try {
      const payload = {
        action: 'get-departments',
        page: 1,
        limit: 50,
        status: 'active'
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success && result?.data) {
        setDepartments(result.data || []);
      } else {
        console.error('Failed to fetch departments:', result?.message);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      // Don't show error toast as this is a background operation
    } finally {
      setLoadingDepartments(false);
    }
  };

  // Fetch reporting managers from API
  const fetchReportingManagers = async () => {
    if (!API_URL) {
      return;
    }

    setLoadingReportingManagers(true);
    try {
      const payload = {
        action: 'get-reporting-managers',
        page: 1,
        limit: 50,
        status: 'active'
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success && result?.data) {
        setReportingManagers(result.data || []);
      } else {
        console.error('Failed to fetch reporting managers:', result?.message);
      }
    } catch (error) {
      console.error('Error fetching reporting managers:', error);
      // Don't show error toast as this is a background operation
    } finally {
      setLoadingReportingManagers(false);
    }
  };

  // Helper function to prepare education details for API
  const prepareEducationDetails = () => {
    return formData.educationDetails.map((edu) => ({
      qualification: edu.qualification || '',
      boardUniversity: edu.boardUniversity || '',
      yearOfPassing: edu.yearOfPassing || '',
      percentage: edu.percentage || '',
      relevantCertificateNo: edu.relevantCertificateNo || '',
      fieldOfStudy: edu.fieldOfStudy || '',
      nameOfDegree: edu.nameOfDegree || ''
    }));
  };

  // Helper function to get presigned URLs from backend
  const getPresignedUrls = async (fileNames, contentTypes) => {
    if (!API_URL) {
      throw new Error('API URL is not configured');
    }

    // Use employee name from form data
    let employeeName = '';
    if (formData.firstName && formData.lastName) {
      employeeName = `${formData.firstName}_${formData.lastName}`.toLowerCase().replace(/\s+/g, '_');
    } else {
      // If name not available yet, use employee ID or temporary identifier
      employeeName = formData.employeeId ? formData.employeeId.toLowerCase().replace(/\s+/g, '_') : `temp_${Date.now()}`;
    }

    const payload = {
      action: 'get-employee-document-upload-url',
      employeeName: employeeName,
      fileNames: fileNames,
      contentTypes: contentTypes
    };

    const response = await axios.post(API_URL, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.data?.success && response.data?.data) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message || 'Failed to get presigned URLs');
    }
  };

  // Helper function to upload file to S3 using presigned URL
  const uploadFileToS3 = async (file, uploadUrl, contentType) => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to upload file to S3: ${response.statusText}`);
    }

    return true;
  };

  // Helper function to upload a single file and return both S3 key and preview URL
  const uploadFile = async (file, fileType, index = null) => {
    try {
      // Set uploading state
      if (fileType === 'aadharCard') {
        setUploadingFiles(prev => ({ ...prev, aadharCard: true }));
      } else if (fileType === 'panCard') {
        setUploadingFiles(prev => ({ ...prev, panCard: true }));
      } else if (fileType === 'educationCertificate' && index !== null) {
        setUploadingFiles(prev => ({
          ...prev,
          educationCertificates: { ...prev.educationCertificates, [index]: true }
        }));
      }

      // Get file extension
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const contentType = file.type || (fileExtension === 'pdf' ? 'application/pdf' : 'image/jpeg');
      const fileName = file.name;

      // Get presigned URL
      const uploadData = await getPresignedUrls([fileName], [contentType]);
      const fileData = uploadData[0];

      // Upload to S3
      await uploadFileToS3(file, fileData.uploadUrl, fileData.contentType);

      // Extract S3 key - remove query parameters first
      let s3Key = null;
      if (fileData.s3Key) {
        s3Key = fileData.s3Key;
      } else if (fileData.fullUrl) {
        // Remove query parameters from fullUrl before extracting S3 key
        const fullUrlWithoutParams = fileData.fullUrl.split('?')[0];
        const urlParts = fullUrlWithoutParams.split('.com/');
        if (urlParts.length > 1) {
          s3Key = urlParts[1];
        }
      } else {
        // Fallback: extract from uploadUrl
        const uploadUrlWithoutParams = fileData.uploadUrl.split('?')[0];
        const urlParts = uploadUrlWithoutParams.split('.com/');
        if (urlParts.length > 1) {
          s3Key = urlParts[1];
        }
      }

      if (!s3Key) {
        throw new Error('Unable to extract S3 key from API response');
      }

      // Return object with both S3 key and preview URL
      // Use blob URL from file for immediate preview (fullUrl from API is not presigned for viewing)
      // The blob URL will work until the form is submitted, then backend will provide presigned URLs
      const previewUrl = URL.createObjectURL(file);

      // Store contentType for better image detection
      const storedContentType = fileData.contentType || contentType;

      return {
        s3Key: s3Key,
        previewUrl: previewUrl,
        contentType: storedContentType
      };
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      toast.error(`Failed to upload ${fileType === 'aadharCard' ? 'Aadhar Card' : fileType === 'panCard' ? 'PAN Card' : 'Education Certificate'}. Please try again.`);
      throw error;
    } finally {
      // Clear uploading state
      if (fileType === 'aadharCard') {
        setUploadingFiles(prev => ({ ...prev, aadharCard: false }));
      } else if (fileType === 'panCard') {
        setUploadingFiles(prev => ({ ...prev, panCard: false }));
      } else if (fileType === 'educationCertificate' && index !== null) {
        setUploadingFiles(prev => ({
          ...prev,
          educationCertificates: { ...prev.educationCertificates, [index]: false }
        }));
      }
    }
  };

  // Helper function to extract S3 key from full URL
  const extractS3KeyFromUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    
    try {
      // Remove query parameters from the URL first (split at first ?)
      let urlPath = url.split('?')[0];
      
      // Also handle encoded query parameters in the path (%3F = ?, %253F = %3F)
      // Remove anything after %3F or %253F in the path
      urlPath = urlPath.split('%3F')[0].split('%253F')[0].split('%25253F')[0];
      
      // Try to extract S3 key from URL path
      // Pattern: https://bucket-name.s3.region.amazonaws.com/key
      const s3Patterns = [
        /\.s3\.ap-south-1\.amazonaws\.com\/(.+)$/i,  // Match everything after .s3.ap-south-1.amazonaws.com/
        /\.s3\.amazonaws\.com\/(.+)$/i,  // Match everything after .s3.amazonaws.com/
        /\.com\/(.+)$/i,  // Match everything after .com/ (fallback)
        /\.amazonaws\.com\/(.+)$/i  // Match everything after amazonaws.com/ (fallback)
      ];
      
      for (const pattern of s3Patterns) {
        const match = urlPath.match(pattern);
        if (match && match[1]) {
          let s3Key = match[1];
          
          // Remove any encoded query parameters before decoding
          s3Key = s3Key.split('%3F')[0].split('%253F')[0].split('%25253F')[0];
          
          // Decode URL encoding (handle single and double encoding)
          try {
            // Decode multiple times to handle double/triple encoding
            let decoded = s3Key;
            let previousDecoded = '';
            let decodeAttempts = 0;
            while (decoded !== previousDecoded && decodeAttempts < 5) {
              previousDecoded = decoded;
              try {
                decoded = decodeURIComponent(decoded);
                decodeAttempts++;
              } catch (e) {
                break;
              }
            }
            s3Key = decoded;
          } catch (e) {
            // If decoding fails, use the original but clean it
            console.warn('Failed to decode S3 key, using cleaned original:', e);
          }
          
          // Remove any remaining query parameters, fragments, or encoded query strings
          s3Key = s3Key.split('?')[0].split('#')[0].split('%3F')[0].split('%253F')[0].split('%25253F')[0];
          
          // Remove any query-like parameters (X-Amz-*, etc.) - check for both encoded and decoded versions
          const queryPatterns = ['X-Amz-', '%58-Amz-', '%2558-Amz-', 'X%2DAmz%2D'];
          for (const pattern of queryPatterns) {
            if (s3Key.includes(pattern)) {
              const queryIndex = s3Key.indexOf(pattern);
              s3Key = s3Key.substring(0, queryIndex);
              break;
            }
          }
          
          // Also remove any remaining encoded query parameter indicators
          s3Key = s3Key.replace(/%3F.*$/i, '').replace(/%253F.*$/i, '').replace(/%25253F.*$/i, '');
          
          // Ensure it doesn't exceed max length (1024 characters for S3 keys)
          if (s3Key.length > 1024) {
            console.warn('S3 key too long after extraction:', s3Key.length, 'Original URL length:', url.length);
            // Try to extract just the essential parts
            const parts = s3Key.split('/');
            if (parts.length >= 2) {
              // Keep folder structure and clean filename
              const folder = parts[0];
              let filename = parts[parts.length - 1];
              // Clean filename of any query parameters
              filename = filename.split('?')[0].split('&')[0].split('%3F')[0].split('X-Amz-')[0];
              s3Key = folder + '/' + filename;
              
              if (s3Key.length > 1024) {
                // Last resort: just use the filename (truncated if needed)
                s3Key = filename.substring(0, 1024);
              }
            } else {
              // If no folder structure, just truncate
              s3Key = s3Key.substring(0, 1024);
            }
          }
          
          // Final validation - must be under 1024 characters
          if (s3Key.length > 1024) {
            console.error('S3 key still too long after cleanup:', s3Key.length);
            return null;
          }
          
          return s3Key;
        }
      }
      
      // If URL doesn't match S3 pattern, check if it's already an S3 key (no http/https)
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        // Might already be an S3 key, but remove query params if any
        let keyWithoutParams = url.split('?')[0].split('#')[0].split('%3F')[0].split('%253F')[0];
        // Remove query-like parameters
        if (keyWithoutParams.includes('X-Amz-')) {
          keyWithoutParams = keyWithoutParams.substring(0, keyWithoutParams.indexOf('X-Amz-'));
        }
        return keyWithoutParams.length <= 1024 ? keyWithoutParams : null;
      }
      
      return null;
    } catch (error) {
      console.error('Error extracting S3 key from URL:', error, url);
      return null;
    }
  };

  // Helper function to prepare file data for API
  // Expects S3 keys (strings) instead of full URLs
  const prepareFileData = async () => {
    const fileData = {
      aadharCard: null,
      panCard: null,
      educationCertificates: []
    };

    // Aadhar Card - extract S3 key from URL or use stored S3 key
    if (formData.aadharCard) {
      if (typeof formData.aadharCard === 'string' && formData.aadharCard.trim()) {
        // Extract S3 key from URL if it's a full URL, otherwise use as is (already S3 key)
        const s3Key = extractS3KeyFromUrl(formData.aadharCard.trim());
        if (s3Key && s3Key.length <= 1024) {
          fileData.aadharCard = s3Key;
        } else {
          console.error('Invalid S3 key for Aadhar Card:', s3Key ? `Length: ${s3Key.length}` : 'null');
        }
      } else if (formData.aadharCard && typeof formData.aadharCard === 'object' && formData.aadharCard.s3Key) {
        // If it's an object with s3Key property (from new upload)
        const s3Key = formData.aadharCard.s3Key;
        if (s3Key && s3Key.length <= 1024) {
          fileData.aadharCard = s3Key;
        } else {
          console.error('Invalid S3 key for Aadhar Card (from object):', s3Key ? `Length: ${s3Key.length}` : 'null');
        }
      }
    }

    // PAN Card - extract S3 key from URL or use stored S3 key
    if (formData.panCard) {
      if (typeof formData.panCard === 'string' && formData.panCard.trim()) {
        // Extract S3 key from URL if it's a full URL, otherwise use as is (already S3 key)
        const s3Key = extractS3KeyFromUrl(formData.panCard.trim());
        if (s3Key && s3Key.length <= 1024) {
          fileData.panCard = s3Key;
        } else {
          console.error('Invalid S3 key for PAN Card:', s3Key ? `Length: ${s3Key.length}` : 'null');
        }
      } else if (formData.panCard && typeof formData.panCard === 'object' && formData.panCard.s3Key) {
        // If it's an object with s3Key property (from new upload)
        const s3Key = formData.panCard.s3Key;
        if (s3Key && s3Key.length <= 1024) {
          fileData.panCard = s3Key;
        } else {
          console.error('Invalid S3 key for PAN Card (from object):', s3Key ? `Length: ${s3Key.length}` : 'null');
        }
      }
    }

    // Education Certificates - extract S3 keys from URLs or use stored S3 keys
    if (formData.educationCertificates && formData.educationCertificates.length > 0) {
      fileData.educationCertificates = formData.educationCertificates
        .filter(item => item !== null && item !== undefined)
        .map(item => {
          if (typeof item === 'string' && item.trim()) {
            // Extract S3 key from URL if it's a full URL, otherwise use as is (already S3 key)
            const s3Key = extractS3KeyFromUrl(item.trim());
            if (s3Key && s3Key.length <= 1024) {
              return s3Key;
            } else {
              console.error('Invalid S3 key for Education Certificate:', s3Key ? `Length: ${s3Key.length}` : 'null');
              return null;
            }
          } else if (item && typeof item === 'object' && item.s3Key) {
            // If it's an object with s3Key property (from new upload)
            const s3Key = item.s3Key;
            if (s3Key && s3Key.length <= 1024) {
              return s3Key;
            } else {
              console.error('Invalid S3 key for Education Certificate (from object):', s3Key ? `Length: ${s3Key.length}` : 'null');
              return null;
            }
          }
          return null;
        })
        .filter(key => key !== null && key.length <= 1024);
    }

    return fileData;
  };

  // Main submit handler
  const handleApprove = async () => {
    // Validate all employment fields before approving
    const approvalErrors = {};

    if (!formData.employeeId || !formData.employeeId.trim()) {
      approvalErrors.employeeId = 'Employee ID is required';
    }
    if (!formData.employmentType) {
      approvalErrors.employmentType = 'Employment Type is required';
    }
    if (!formData.designation) {
      approvalErrors.designation = 'Designation is required';
    }
    if (!formData.pfUanNumber || !formData.pfUanNumber.trim()) {
      approvalErrors.pfUanNumber = 'PF UAN Number is required';
    }
    if (!formData.dateOfJoining) {
      approvalErrors.dateOfJoining = 'Date of Joining is required';
    }
    if (!formData.employmentStatus) {
      approvalErrors.employmentStatus = 'Employee Status is required';
    }

    // Set errors and show validation messages
    if (Object.keys(approvalErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...approvalErrors }));
      toast.error('Please fill all required employment fields before approving');
      return;
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    if (!id) {
      toast.error('Employee ID is missing');
      return;
    }

    // Validate basic info and addresses before approving
    if (!validateBasicInfo()) {
      toast.error('Please fill all required fields in Basic Info');
      setActiveTab('basic');
      return;
    }

    if (!formData.addresses || formData.addresses.length === 0) {
      toast.error('Please add at least one address');
      setActiveTab('basic');
      return;
    }

    setLoading(true);
    try {
      // Prepare file data
      const fileData = await prepareFileData();

      // Prepare the complete payload for approval (same as handleSubmit but with status set to active)
      const payload = {
        action: 'update-employee',
        id: id,
        // Basic Info - Required
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        gender: formData.gender,
        dateOfBirth: formatDateToDDMMYYYY(formData.dateOfBirth),
        citizenship: formData.citizenship,
        email: formData.email.trim(),
        mobileNumber: formData.mobileNumber,
        maritalStatus: formData.maritalStatus,
        // Basic Info - Optional
        ...(formData.middleName && { middleName: formData.middleName.trim() }),
        ...(formData.bloodGroup && { bloodGroup: formData.bloodGroup }),
        ...(formData.aadharNumber && { aadharNumber: formData.aadharNumber }),
        ...(formData.panNumber && { panNumber: formData.panNumber.toUpperCase() }),
        ...(formData.countryCode && { countryCode: formData.countryCode }),
        // Addresses - Required
        addresses: prepareAddresses(),
        // Employment Details - All required for approval
        employeeId: formData.employeeId.trim(),
        employmentType: formData.employmentType,
        designation: formData.designation,
        ...(formData.designation && {
          designationName: designations.find(d => d.id === formData.designation)?.name || ''
        }),
        ...(formData.department && {
          department: formData.department,
          departmentName: departments.find(d => d.id === formData.department)?.name || ''
        }),
        ...(formData.reportingManager && {
          reportingManager: formData.reportingManager,
          reportingManagerName: reportingManagers.find(r => r.id === formData.reportingManager)?.name || ''
        }),
        pfUanNumber: formData.pfUanNumber.trim(),
        ...(formData.pfNumber && { pfNumber: formData.pfNumber }),
        ...(formData.esiNumber && { esiNumber: formData.esiNumber }),
        dateOfJoining: formatDateToDDMMYYYY(formData.dateOfJoining),
        employmentStatus: 'active', // Change status to active on approval
        pfApplicable: formData.pfApplicable || false,
        tdsApplicable: formData.tdsApplicable || false,
        remoteWorkAllowed: formData.remoteWorkAllowed || false,
        checkInCheckOutNotRequired: formData.checkInCheckOutNotRequired || false,
        // Experiences - Optional
        ...(formData.experiences.length > 0 && { experiences: prepareExperiences() }),
        // Education Details - Optional
        ...(formData.educationDetails.length > 0 && { educationDetails: prepareEducationDetails() }),
        // Bank Details - Optional
        ...(formData.bankName && { bankName: formData.bankName.trim() }),
        ...(formData.accountNumber && { accountNumber: formData.accountNumber }),
        ...(formData.ifscCode && { ifscCode: formData.ifscCode.toUpperCase() }),
        ...(formData.branchName && { branchName: formData.branchName.trim() }),
        ...(formData.accountHolderName && { accountHolderName: formData.accountHolderName.trim() }),
        // Documents - Optional (S3 Keys)
        // Validate S3 keys before sending to API
        ...(fileData.aadharCard && fileData.aadharCard.length <= 1024 && { aadharCard: fileData.aadharCard }),
        ...(fileData.panCard && fileData.panCard.length <= 1024 && { panCard: fileData.panCard }),
        ...(fileData.educationCertificates.length > 0 && {
          educationCertificates: fileData.educationCertificates.filter(key => key && key.length <= 1024)
        }),
        // Other - Optional
        ...(localStorage.getItem('id') && { updatedBy: localStorage.getItem('id') })
      };

      // Send API request
      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result.message || 'Employee approved successfully!');

        // Update local state
        setFormData(prev => ({ ...prev, employmentStatus: 'active' }));
        setOriginalStatus('active');

        // Navigate back to employee list
        navigate('/employee');
      } else {
        const errorMessage = result?.message || 'Failed to approve employee. Please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error approving employee:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all required fields
    if (!validateBasicInfo()) {
      toast.error('Please fill all required fields in Basic Info');
      setActiveTab('basic');
      return;
    }

    if (!formData.addresses || formData.addresses.length === 0) {
      toast.error('Please add at least one address');
      setActiveTab('basic');
      return;
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    // Check if any files are still uploading
    if (uploadingFiles.aadharCard || uploadingFiles.panCard ||
      Object.values(uploadingFiles.educationCertificates).some(uploading => uploading)) {
      toast.error('Please wait for file uploads to complete');
      return;
    }

    // Check if files are File objects (not yet uploaded) - this shouldn't happen but handle it
    if (formData.aadharCard && formData.aadharCard instanceof File) {
      toast.error('Please wait for Aadhar Card to finish uploading');
      return;
    }
    if (formData.panCard && formData.panCard instanceof File) {
      toast.error('Please wait for PAN Card to finish uploading');
      return;
    }
    if (formData.educationCertificates.some(cert => cert instanceof File)) {
      toast.error('Please wait for all education certificates to finish uploading');
      return;
    }

    try {
      // Prepare file data (S3 keys)
      const fileData = await prepareFileData();

      if (!id) {
        toast.error('Employee ID is missing');
        return;
      }

      // Prepare the payload according to API requirements
      const payload = {
        action: 'update-employee',
        id: id,
        // Basic Info - Required
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        gender: formData.gender,
        dateOfBirth: formatDateToDDMMYYYY(formData.dateOfBirth),
        citizenship: formData.citizenship,
        email: formData.email.trim(),
        mobileNumber: formData.mobileNumber,
        maritalStatus: formData.maritalStatus,
        // Basic Info - Optional
        ...(formData.middleName && { middleName: formData.middleName.trim() }),
        ...(formData.bloodGroup && { bloodGroup: formData.bloodGroup }),
        ...(formData.aadharNumber && { aadharNumber: formData.aadharNumber }),
        ...(formData.panNumber && { panNumber: formData.panNumber.toUpperCase() }),
        ...(formData.countryCode && { countryCode: formData.countryCode }),
        // Addresses - Required
        addresses: prepareAddresses(),
        // Employment Details - Optional
        ...(formData.employeeId && { employeeId: formData.employeeId.trim() }),
        ...(formData.employmentType && { employmentType: formData.employmentType }),
        ...(formData.designation && {
          designation: formData.designation,
          designationName: designations.find(d => d.id === formData.designation)?.name || ''
        }),
        ...(formData.department && {
          department: formData.department,
          departmentName: departments.find(d => d.id === formData.department)?.name || ''
        }),
        ...(formData.reportingManager && {
          reportingManager: formData.reportingManager,
          reportingManagerName: reportingManagers.find(r => r.id === formData.reportingManager)?.name || ''
        }),
        ...(formData.pfUanNumber && { pfUanNumber: formData.pfUanNumber }),
        ...(formData.pfNumber && { pfNumber: formData.pfNumber }),
        ...(formData.esiNumber && { esiNumber: formData.esiNumber }),
        ...(formData.dateOfJoining && { dateOfJoining: formatDateToDDMMYYYY(formData.dateOfJoining) }),
        ...(formData.employmentStatus && { employmentStatus: formData.employmentStatus }),
        pfApplicable: formData.pfApplicable || false,
        tdsApplicable: formData.tdsApplicable || false,
        remoteWorkAllowed: formData.remoteWorkAllowed || false,
        checkInCheckOutNotRequired: formData.checkInCheckOutNotRequired || false,
        // Experiences - Optional
        ...(formData.experiences.length > 0 && { experiences: prepareExperiences() }),
        // Education Details - Optional
        ...(formData.educationDetails.length > 0 && { educationDetails: prepareEducationDetails() }),
        // Bank Details - Optional
        ...(formData.bankName && { bankName: formData.bankName.trim() }),
        ...(formData.accountNumber && { accountNumber: formData.accountNumber }),
        ...(formData.ifscCode && { ifscCode: formData.ifscCode.toUpperCase() }),
        ...(formData.branchName && { branchName: formData.branchName.trim() }),
        ...(formData.accountHolderName && { accountHolderName: formData.accountHolderName.trim() }),
        // Documents - Optional (S3 Keys)
        // Validate S3 keys before sending to API
        ...(fileData.aadharCard && fileData.aadharCard.length <= 1024 && { aadharCard: fileData.aadharCard }),
        ...(fileData.panCard && fileData.panCard.length <= 1024 && { panCard: fileData.panCard }),
        ...(fileData.educationCertificates.length > 0 && {
          educationCertificates: fileData.educationCertificates.filter(key => key && key.length <= 1024)
        }),
        // Other - Optional
        ...(localStorage.getItem('id') && { updatedBy: localStorage.getItem('id') })
      };

      // Send API request
      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result.message || 'Employee updated successfully!');

        // Navigate back to employee list
        navigate('/employee');
      } else {
        const errorMessage = result?.message || 'Failed to update employee. Please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error updating employee:', error);

      let errorMessage = 'Something went wrong. Please try again.';

      if (error.response) {
        // Server responded with error status
        const responseData = error.response.data;
        errorMessage = responseData?.message || `Error: ${error.response.status} ${error.response.statusText}`;

        // Handle specific error cases
        if (error.response.status === 409) {
          errorMessage = responseData?.message || 'Email already registered';
        } else if (error.response.status === 400) {
          errorMessage = responseData?.message || 'Invalid data. Please check all fields.';
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      toast.error(errorMessage);
    }
  };

  // Validate Basic Info Tab
  const validateBasicInfo = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First Name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last Name is required';
    }
    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of Birth is required';
    }
    if (!formData.citizenship) {
      newErrors.citizenship = 'Citizenship is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.mobileNumber || formData.mobileNumber.length !== 10) {
      newErrors.mobileNumber = 'Valid 10-digit mobile number is required';
    }
    if (!formData.maritalStatus) {
      newErrors.maritalStatus = 'Marital Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate Employment Tab
  const validateEmployment = () => {
    const newErrors = {};

    if (!formData.employmentType) {
      newErrors.employmentType = 'Employment Type is required';
    }
    if (!formData.designation) {
      newErrors.designation = 'Designation is required';
    }
    if (!formData.dateOfJoining) {
      newErrors.dateOfJoining = 'Date of Joining is required';
    }
    if (!formData.employmentStatus) {
      newErrors.employmentStatus = 'Employee Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate Education Tab
  const validateEducation = () => {
    if (formData.educationDetails.length === 0) {
      toast.error('Please add at least one education record');
      return false;
    }

    for (let i = 0; i < formData.educationDetails.length; i++) {
      const edu = formData.educationDetails[i];
      // All fields are mandatory except fieldOfStudy
      if (!edu.qualification || !edu.boardUniversity || !edu.yearOfPassing || !edu.percentage || !edu.relevantCertificateNo) {
        toast.error('Please fill all required fields for education record ' + (i + 1));
        return false;
      }
    }

    return true;
  };

  // Validate Bank Tab
  const validateBank = () => {
    const newErrors = {};

    if (!formData.bankName.trim()) {
      newErrors.bankName = 'Bank Name is required';
    }
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account Number is required';
    }
    if (!formData.ifscCode.trim()) {
      newErrors.ifscCode = 'IFSC Code is required';
    }
    if (!formData.accountHolderName.trim()) {
      newErrors.accountHolderName = 'Account Holder Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate Document Tab (optional - documents are not required by API)
  const validateDocument = () => {
    // Documents are optional according to API, but we can show warnings if they're not uploaded
    // For File objects, they need to be uploaded first to get URLs
    // For now, we'll allow submission even without documents
    return true;
  };

  // Handle Save and Next for Basic Info
  const handleBasicInfoSaveAndNext = (e) => {
    e.preventDefault();
    if (validateBasicInfo()) {
      // Check if at least one address is added
      if (!formData.addresses || formData.addresses.length === 0) {
        toast.error('Please add at least one address before proceeding');
        return;
      }
      toast.success('Basic info saved successfully!');
      // Mark employment tab as accessible
      setValidatedTabs(prev => {
        if (!prev.includes('employment')) {
          return [...prev, 'employment'];
        }
        return prev;
      });
      setActiveTab('employment');
    } else {
      toast.error('Please fill all required fields');
    }
  };

  // Check if mandatory fields are filled (for address button)
  const areMandatoryFieldsFilled = () => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.gender !== '' &&
      formData.dateOfBirth !== '' &&
      formData.citizenship !== '' &&
      formData.email.trim() !== '' &&
      formData.mobileNumber.length === 10 &&
      formData.maritalStatus !== ''
    );
  };

  const handleAddAddress = () => {
    if (!areMandatoryFieldsFilled()) {
      toast.error('Please fill all mandatory fields before adding address');
      return;
    }
    setShowAddressModal(true);
  };

  const handleCloseAddressModal = () => {
    setShowAddressModal(false);
    setShowStateDropdown(false);
    setStateSearchTerm('');
    setShowAddressTypeDropdown(false);
    setAddressTypeSearchTerm('');
    setShowCountryDropdown(false);
    setCountrySearchTerm('');
    setAddressData({
      addressType: '',
      address1: '',
      address2: '',
      country: 'India',
      state: 'Tamil Nadu',
      city: '',
      pincode: '',
      sameAsPermanent: false,
      editingAddressId: null // Reset editing ID when closing
    });
    setAddressErrors({});
  };

  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddressData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (addressErrors[name]) {
      setAddressErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleStateSelect = (state) => {
    setAddressData(prev => ({
      ...prev,
      state: state
    }));
    setShowStateDropdown(false);
    setStateSearchTerm('');
    if (addressErrors.state) {
      setAddressErrors(prev => ({
        ...prev,
        state: ''
      }));
    }
  };

  const handleStateKeyDown = (e) => {
    if (e.key === 'Enter' && filteredStates.length > 0) {
      e.preventDefault();
      handleStateSelect(filteredStates[0]);
    } else if (e.key === 'Escape') {
      setShowStateDropdown(false);
      setStateSearchTerm('');
    } else if (e.key === 'Tab') {
      // Allow default tab behavior
      setShowStateDropdown(false);
      setStateSearchTerm('');
    }
  };

  const handleStateDisplayKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setShowStateDropdown(true);
      setStateSearchTerm('');
    } else if (e.key === 'Tab') {
      // Allow default tab behavior - will move to next field
      setShowStateDropdown(false);
    }
  };

  const handleAddressTypeSelect = (type) => {
    setAddressData(prev => ({
      ...prev,
      addressType: type.toLowerCase().replace(' address', '')
    }));
    setShowAddressTypeDropdown(false);
    setAddressTypeSearchTerm('');
    if (addressErrors.addressType) {
      setAddressErrors(prev => ({
        ...prev,
        addressType: ''
      }));
    }
  };

  const handleAddressTypeKeyDown = (e) => {
    if (e.key === 'Enter' && filteredAddressTypes.length > 0) {
      e.preventDefault();
      handleAddressTypeSelect(filteredAddressTypes[0]);
    } else if (e.key === 'Escape') {
      setShowAddressTypeDropdown(false);
      setAddressTypeSearchTerm('');
    } else if (e.key === 'Tab') {
      // Allow default tab behavior
      setShowAddressTypeDropdown(false);
      setAddressTypeSearchTerm('');
    }
  };

  const handleAddressTypeDisplayKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setShowAddressTypeDropdown(true);
      setAddressTypeSearchTerm('');
    } else if (e.key === 'Tab') {
      // Allow default tab behavior - will move to next field
      setShowAddressTypeDropdown(false);
    }
  };

  const handleCountrySelect = (country) => {
    setAddressData(prev => ({
      ...prev,
      country: country
    }));
    setShowCountryDropdown(false);
    setCountrySearchTerm('');
    if (addressErrors.country) {
      setAddressErrors(prev => ({
        ...prev,
        country: ''
      }));
    }
  };

  const handleCountryKeyDown = (e) => {
    if (e.key === 'Enter' && filteredCountries.length > 0) {
      e.preventDefault();
      handleCountrySelect(filteredCountries[0]);
    } else if (e.key === 'Escape') {
      setShowCountryDropdown(false);
      setCountrySearchTerm('');
    } else if (e.key === 'Tab') {
      // Allow default tab behavior
      setShowCountryDropdown(false);
      setCountrySearchTerm('');
    }
  };

  const handleCountryDisplayKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setShowCountryDropdown(true);
      setCountrySearchTerm('');
    } else if (e.key === 'Tab') {
      // Allow default tab behavior - will move to next field
      setShowCountryDropdown(false);
    }
  };

  const filteredStates = indianStates.filter(state =>
    state.toLowerCase().includes(stateSearchTerm.toLowerCase())
  );

  const filteredAddressTypes = addressTypes.filter(type =>
    type.toLowerCase().includes(addressTypeSearchTerm.toLowerCase())
  );

  const filteredCountries = countries.filter(country =>
    country.toLowerCase().includes(countrySearchTerm.toLowerCase())
  );

  const handlePincodeChange = (e) => {
    const value = e.target.value;
    // Only allow numeric characters and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setAddressData(prev => ({
      ...prev,
      pincode: numericValue
    }));
    if (addressErrors.pincode) {
      setAddressErrors(prev => ({
        ...prev,
        pincode: ''
      }));
    }
  };

  const handleSaveAddress = (e) => {
    e.preventDefault();

    // Validation - addressType not required if sameAsPermanent is checked
    const newErrors = {};
    if (!addressData.sameAsPermanent && !addressData.addressType) {
      newErrors.addressType = 'Address type is required';
    }
    if (!addressData.address1.trim()) {
      newErrors.address1 = 'Address 1 is required';
    }
    if (!addressData.country) {
      newErrors.country = 'Country is required';
    }
    if (!addressData.state) {
      newErrors.state = 'State is required';
    }
    if (!addressData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!addressData.pincode || addressData.pincode.length !== 6) {
      newErrors.pincode = 'Valid 6-digit pincode is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setAddressErrors(newErrors);
      toast.error('Please fill all required fields');
      return false; // Return false if validation fails
    }

    // Prepare address data without sameAsPermanent flag
    const addressToSave = {
      address1: addressData.address1,
      address2: addressData.address2,
      country: addressData.country,
      state: addressData.state,
      city: addressData.city,
      pincode: addressData.pincode,
      updatedAt: Date.now()
    };

    // If "same as permanent" is checked, save both permanent and present addresses
    if (addressData.sameAsPermanent) {
      // Remove existing permanent and present addresses if they exist
      setFormData(prev => {
        const filteredAddresses = prev.addresses.filter(
          addr => addr.addressType !== 'permanent' && addr.addressType !== 'present'
        );

        const permanentAddress = {
          ...addressToSave,
          addressType: 'permanent',
          id: addressData.editingAddressId || Date.now() // Use existing ID if editing
        };
        const presentAddress = {
          ...addressToSave,
          addressType: 'present',
          id: addressData.editingAddressId ? (addressData.editingAddressId + 1) : (Date.now() + 1) // Use existing ID if editing
        };

        return {
          ...prev,
          addresses: [...filteredAddresses, permanentAddress, presentAddress]
        };
      });

      toast.success('Permanent and Present addresses added successfully!');
    } else {
      // Remove existing address of the same type if it exists (or the one being edited)
      setFormData(prev => {
        const filteredAddresses = prev.addresses.filter(
          addr => {
            // If editing, remove the address being edited
            if (addressData.editingAddressId && addr.id === addressData.editingAddressId) {
              return false;
            }
            // Otherwise, remove addresses of the same type
            return addr.addressType !== addressData.addressType;
          }
        );

        const newAddress = {
          ...addressToSave,
          addressType: addressData.addressType,
          id: addressData.editingAddressId || Date.now() // Use existing ID if editing, otherwise create new
        };

        return {
          ...prev,
          addresses: [...filteredAddresses, newAddress]
        };
      });

      toast.success(addressData.editingAddressId ? 'Address updated successfully!' : 'Address added successfully!');
    }

    handleCloseAddressModal();
    return true; // Return true after successful save
  };

  const handleSaveAddressButton = (e) => {
    e.preventDefault();
    handleSaveAddress(e);
    // Just save and close modal, don't navigate to next tab
  };

  const handleDeleteAddress = (addressId) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.filter(addr => addr.id !== addressId)
    }));
    toast.success('Address deleted successfully!');
  };

  const handleEditAddress = (address) => {
    // Set the address data for editing
    setAddressData({
      addressType: address.addressType,
      address1: address.address1,
      address2: address.address2,
      country: address.country,
      state: address.state,
      city: address.city,
      pincode: address.pincode,
      sameAsPermanent: false,
      editingAddressId: address.id // Store the ID of the address being edited
    });
    // Open the modal for editing
    setShowAddressModal(true);
  };


  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleAddExperience = () => {
    const newExperience = {
      id: Date.now(),
      companyName: '',
      designation: '',
      periodFrom: '',
      periodTo: '',
      isPresent: false, // Only first experience can be "Present"
      duration: ''
    };
    setFormData(prev => ({
      ...prev,
      experiences: [...prev.experiences, newExperience]
    }));
  };

  const handleExperienceChange = (index, field, value) => {
    setFormData(prev => {
      const updatedExperiences = [...prev.experiences];
      updatedExperiences[index] = {
        ...updatedExperiences[index],
        [field]: value
      };

      // Calculate duration if period dates changed
      if (field === 'periodFrom' || field === 'periodTo' || field === 'isPresent') {
        const experience = updatedExperiences[index];
        if (experience.periodFrom) {
          const fromDate = new Date(experience.periodFrom);
          if (!isNaN(fromDate.getTime())) {
            let toDate;
            if (experience.isPresent) {
              toDate = new Date();
            } else if (experience.periodTo) {
              toDate = new Date(experience.periodTo);
              if (isNaN(toDate.getTime())) {
                updatedExperiences[index].duration = '';
                return {
                  ...prev,
                  experiences: updatedExperiences
                };
              }
            } else {
              updatedExperiences[index].duration = '';
              return {
                ...prev,
                experiences: updatedExperiences
              };
            }

            // Calculate difference
            const fromYear = fromDate.getFullYear();
            const fromMonth = fromDate.getMonth();
            const toYear = toDate.getFullYear();
            const toMonth = toDate.getMonth();

            let years = toYear - fromYear;
            let months = toMonth - fromMonth;

            // Adjust if end day is before start day
            if (months < 0) {
              years--;
              months += 12;
            }

            if (toDate.getDate() < fromDate.getDate()) {
              months--;
              if (months < 0) {
                years--;
                months += 12;
              }
            }

            let duration = '';
            if (years === 0 && months === 0) {
              const diffTime = toDate - fromDate;
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              duration = `${diffDays} day${diffDays > 1 ? 's' : ''}`;
            } else if (years === 0) {
              duration = `${months} month${months > 1 ? 's' : ''}`;
            } else if (months === 0) {
              duration = `${years} year${years > 1 ? 's' : ''}`;
            } else {
              duration = `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
            }

            updatedExperiences[index].duration = duration;
          } else {
            updatedExperiences[index].duration = '';
          }
        } else {
          updatedExperiences[index].duration = '';
        }
      }

      return {
        ...prev,
        experiences: updatedExperiences
      };
    });
  };

  const handleRemoveExperience = (id) => {
    setFormData(prev => ({
      ...prev,
      experiences: prev.experiences.filter(exp => exp.id !== id)
    }));
  };


  const getPermanentAddress = () => {
    return formData.addresses?.find(addr => addr.addressType === 'permanent');
  };

  const getPresentAddress = () => {
    return formData.addresses?.find(addr => addr.addressType === 'present');
  };

  const handleAddEducation = () => {
    const newEducation = {
      id: Date.now(),
      qualification: '',
      boardUniversity: '',
      yearOfPassing: '',
      percentage: '',
      relevantCertificateNo: '',
      fieldOfStudy: '',
      nameOfDegree: ''
    };
    setFormData(prev => ({
      ...prev,
      educationDetails: [...prev.educationDetails, newEducation]
    }));
  };

  const handleEducationChange = (index, field, value) => {
    let validatedValue = value;

    // Validate Year of Passing - only 4 digits, no alphabets
    if (field === 'yearOfPassing') {
      // Only allow numeric characters, max 4 digits
      validatedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    // Validate Percentage/CGPA - only numeric with decimal point, no alphabets
    if (field === 'percentage') {
      // Allow numbers and one decimal point
      validatedValue = value.replace(/[^\d.]/g, '');
      // Ensure only one decimal point
      const parts = validatedValue.split('.');
      if (parts.length > 2) {
        validatedValue = parts[0] + '.' + parts.slice(1).join('');
      }
    }

    setFormData(prev => {
      const updatedEducation = [...prev.educationDetails];
      const currentEducation = updatedEducation[index];

      // If qualification is changed, clear fieldOfStudy and nameOfDegree if not applicable
      if (field === 'qualification') {
        updatedEducation[index] = {
          ...currentEducation,
          [field]: validatedValue,
          // Clear fieldOfStudy if 10th is selected
          ...(validatedValue === '10th' && { fieldOfStudy: '' }),
          // Clear nameOfDegree if 10th or 12th is selected
          ...((validatedValue === '10th' || validatedValue === '12th') && { nameOfDegree: '' })
        };
      } else {
        updatedEducation[index] = {
          ...currentEducation,
          [field]: validatedValue
        };
      }

      return {
        ...prev,
        educationDetails: updatedEducation
      };
    });
  };

  const handleRemoveEducation = (id) => {
    setFormData(prev => ({
      ...prev,
      educationDetails: prev.educationDetails.filter(edu => edu.id !== id)
    }));
  };


  if (loading && initialLoad) {
    return (
      <div className="employee-form">
        <section className="content">
          <div className="container-fluid">
            <Loading size={150} message="Loading employee data..." />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title and Tabs */}
          <div className="row mb-3 employee-header-row">
            {/* Left Side - Employee Title and Description */}
            <div className="col-md-5">
              <h1 className="employee-title">
                Edit Employee
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                Update employee information and details.
              </p>
            </div>

            {/* Right Side - Tabs Navigation */}
            <div className="col-md-7 tabs-container">
              <ul className="nav nav-tabs employee-tabs-list" id="employee-tabs" role="tablist">
                {tabs.map((tab) => {
                  const isAccessible = validatedTabs.includes(tab.id);
                  const tabIndex = tabs.findIndex(t => t.id === tab.id);

                  return (
                    <li className="nav-item" key={tab.id}>
                      <div
                        className={`nav-link employee-tab-link ${activeTab === tab.id ? 'active' : ''} ${!isAccessible ? 'disabled' : ''}`}
                        onClick={() => {
                          if (isAccessible) {
                            setActiveTab(tab.id);
                          } else {
                            toast.error(`Please complete ${tabs[tabIndex - 1]?.label || 'previous'} tab first`);
                          }
                        }}
                      >
                        {tab.label}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Tabs Content Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
                <form onSubmit={handleSubmit}>
                  {/* Basic Info Section */}
                  <div className="mb-4">
                    <h4 className="section-title">Basic Info</h4>
                    <p className="section-subtitle">
                      Fill Employee's profile details below
                    </p>

                    <div className="form-row-3">
                      {/* Column 1 */}
                      <div className="form-group">
                        <label htmlFor="firstName" className="form-label">
                          First Name<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="Enter first name"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="middleName" className="form-label">Middle Name</label>
                        <input
                          type="text"
                          id="middleName"
                          name="middleName"
                          className="form-control"
                          value={formData.middleName}
                          onChange={handleChange}
                          placeholder="Enter middle name"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="lastName" className="form-label">
                          Last Name<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Enter last name"
                        />
                      </div>

                      {/* Column 2 */}
                      <div className="form-group">
                        <label htmlFor="dateOfBirth" className="form-label">
                          Date Of Birth<span className="required-star">*</span>
                        </label>
                        <div className="date-input-wrapper">
                          <input
                            type="date"
                            id="dateOfBirth"
                            name="dateOfBirth"
                            className={`form-control ${errors.dateOfBirth ? 'is-invalid' : ''}`}
                            value={formData.dateOfBirth}
                            onChange={handleChange}
                          />
                          <i className="fas fa-calendar-alt date-icon"></i>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="gender" className="form-label">
                          Gender<span className="required-star">*</span>
                        </label>
                        <select
                          id="gender"
                          name="gender"
                          className={`form-control ${errors.gender ? 'is-invalid' : ''}`}
                          value={formData.gender}
                          onChange={handleChange}
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="bloodGroup" className="form-label">Blood Group</label>
                        <select
                          id="bloodGroup"
                          name="bloodGroup"
                          className="form-control"
                          value={formData.bloodGroup}
                          onChange={handleChange}
                        >
                          <option value="">Select Your Blood Group</option>
                          <option value="A+">A+</option>
                          <option value="A-">A-</option>
                          <option value="B+">B+</option>
                          <option value="B-">B-</option>
                          <option value="AB+">AB+</option>
                          <option value="AB-">AB-</option>
                          <option value="O+">O+</option>
                          <option value="O-">O-</option>
                        </select>
                      </div>

                      {/* Column 3 */}
                      <div className="form-group">
                        <label htmlFor="citizenship" className="form-label">
                          Citizenship<span className="required-star">*</span>
                        </label>
                        <select
                          id="citizenship"
                          name="citizenship"
                          className={`form-control ${errors.citizenship ? 'is-invalid' : ''}`}
                          value={formData.citizenship}
                          onChange={handleChange}
                        >
                          <option value="">Select Citizenship</option>
                          <option value="indian">Indian</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="email" className="form-label">
                          Email<span className="required-star">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter email address"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="mobileNumber" className="form-label">
                          Mobile Number<span className="required-star">*</span>
                        </label>
                        <div className="mobile-input-group">
                          <span className="country-code-prefix">+91</span>
                          <input
                            type="tel"
                            id="mobileNumber"
                            name="mobileNumber"
                            className={`form-control mobile-input ${errors.mobileNumber ? 'is-invalid' : ''}`}
                            value={formData.mobileNumber}
                            onChange={handleMobileNumberChange}
                            placeholder="Enter 10 digit mobile number"
                            maxLength="10"
                            inputMode="numeric"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="maritalStatus" className="form-label">
                          Marital Status<span className="required-star">*</span>
                        </label>
                        <select
                          id="maritalStatus"
                          name="maritalStatus"
                          className={`form-control ${errors.maritalStatus ? 'is-invalid' : ''}`}
                          value={formData.maritalStatus}
                          onChange={handleChange}
                        >
                          <option value="">Select Marital Status</option>
                          <option value="single">Single</option>
                          <option value="married">Married</option>

                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="aadharNumber" className="form-label">Aadhar Number</label>
                        <input
                          type="tel"
                          id="aadharNumber"
                          inputMode="numeric"
                          name="aadharNumber"
                          className="form-control"
                          value={formData.aadharNumber}
                          onChange={handleChange}
                          placeholder="Enter Aadhar number"
                          maxLength="12"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="panNumber" className="form-label">PAN Number</label>
                        <input
                          type="text"
                          id="panNumber"
                          name="panNumber"
                          className="form-control text-uppercase"
                          value={formData.panNumber}
                          onChange={handleChange}
                          placeholder="Enter Pan number"
                          maxLength="10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address Section */}
                  <div className="section-divider">
                    <div className="section-header">
                      <div className="section-header-content">
                        <h4>Address</h4>
                        <p>Employee's permanent & present address</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddAddress}
                        className={`btn-add ${areMandatoryFieldsFilled() ? 'btn-enabled' : 'btn-disabled'}`}
                        disabled={!areMandatoryFieldsFilled()}
                      >
                        Add
                      </button>
                    </div>

                    {/* Display Saved Addresses - Side by Side */}
                    {(getPermanentAddress() || getPresentAddress()) && (
                      <div className="address-list address-list-spacing">
                        {/* Permanent Address Card */}
                        <div className="address-card">
                          <div className="address-card-header">
                            <h5 className="address-card-title">
                              Permanent Address
                            </h5>
                            <div className="address-actions">
                              <button
                                type="button"
                                onClick={() => handleEditAddress(getPermanentAddress())}
                                className="address-action-btn"
                                title="Edit address"
                              >
                                <i className="fas fa-pencil-alt"></i>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteAddress(getPermanentAddress()?.id)}
                                className="address-action-btn"
                                title="Delete address"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          </div>
                          {getPermanentAddress() ? (
                            <>
                              <div className="address-content">
                                <p>{getPermanentAddress().city},</p>
                                <p>{getPermanentAddress().city}, {getPermanentAddress().state}</p>
                                <p>{getPermanentAddress().country} - {getPermanentAddress().pincode}</p>
                              </div>
                              <p className="address-update-text">
                                Last update on {formatDate(getPermanentAddress().updatedAt || getPermanentAddress().id)}
                              </p>
                            </>
                          ) : (
                            <div className="address-empty-state">
                              No permanent address
                            </div>
                          )}
                        </div>

                        {/* Present Address Card */}
                        <div className="address-card">
                          <div className="address-card-header">
                            <h5 className="address-card-title">
                              Present Address
                            </h5>
                            <div className="address-actions">
                              <button
                                type="button"
                                onClick={() => handleEditAddress(getPresentAddress())}
                                className="address-action-btn"
                                title="Edit address"
                              >
                                <i className="fas fa-pencil-alt"></i>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteAddress(getPresentAddress()?.id)}
                                className="address-action-btn"
                                title="Delete address"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          </div>
                          {getPresentAddress() ? (
                            <>
                              <div className="address-content">
                                <p>{getPresentAddress().city},</p>
                                <p>{getPresentAddress().city}, {getPresentAddress().state}</p>
                                <p>{getPresentAddress().country} - {getPresentAddress().pincode}</p>
                              </div>
                              <p className="address-update-text">
                                Last update on {formatDate(getPresentAddress().updatedAt || getPresentAddress().id)}
                              </p>
                            </>
                          ) : (
                            <div className="address-empty-state">
                              No present address
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {(!getPermanentAddress() && !getPresentAddress()) && (
                      <div className="address-empty-container">
                        <i className="fas fa-map-marker-alt address-empty-icon"></i>
                        No addresses added yet. Click "Add" to add an address.
                      </div>
                    )}
                  </div>


                  {/* Submit Button */}
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => navigate('/employee')}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`btn-primary ${(!formData.addresses || formData.addresses.length === 0) ? 'btn-disabled' : 'btn-enabled'}`}
                      onClick={handleBasicInfoSaveAndNext}
                      disabled={!formData.addresses || formData.addresses.length === 0}
                    >
                      Save and Next
                    </button>
                  </div>
                </form>
              )}

              {/* Employment Tab */}
              {activeTab === 'employment' && (
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <h4 className="section-title">Employment Details</h4>
                    <p className="section-subtitle">
                      Add employee's employment information
                    </p>

                    <div className="form-row-3">
                      {/* Column 1 */}
                      <div className="form-group">
                        <label htmlFor="employeeId" className="form-label">
                          Employee ID
                        </label>
                        <input
                          type="text"
                          id="employeeId"
                          name="employeeId"
                          className="form-control"
                          value={formData.employeeId}
                          onChange={handleChange}
                          placeholder="----------"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="employmentType" className="form-label">
                          Employment Type<span className="required-star">*</span>
                        </label>
                        <select
                          id="employmentType"
                          name="employmentType"
                          className={`form-control ${errors.employmentType ? 'is-invalid' : ''}`}
                          value={formData.employmentType}
                          onChange={handleChange}
                        >
                          <option value="">Select Employment Type</option>
                          <option value="full-time">Full Time</option>
                          <option value="part-time">Part Time</option>
                          <option value="internship">Internship</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="designation" className="form-label">
                          Designation<span className="required-star">*</span>
                        </label>
                        <select
                          id="designation"
                          name="designation"
                          className={`form-control ${errors.designation ? 'is-invalid' : ''}`}
                          value={formData.designation}
                          onChange={handleChange}
                          disabled={loadingDesignations}
                        >
                          <option value="">
                            {loadingDesignations ? 'Loading designations...' : 'Select the Designation'}
                          </option>
                          {designations.map((designation) => (
                            <option key={designation.id} value={designation.id}>
                              {designation.name}
                            </option>
                          ))}
                        </select>
                        {loadingDesignations && (
                          <small className="form-text text-muted">
                            <i className="fas fa-spinner fa-spin"></i> Loading designations...
                          </small>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="department" className="form-label">
                          Department
                        </label>
                        <select
                          id="department"
                          name="department"
                          className="form-control"
                          value={formData.department}
                          onChange={handleChange}
                          disabled={loadingDepartments}
                        >
                          <option value="">
                            {loadingDepartments ? 'Loading departments...' : 'Select Department'}
                          </option>
                          {departments.map((department) => (
                            <option key={department.id} value={department.id}>
                              {department.name}
                            </option>
                          ))}
                        </select>
                        {loadingDepartments && (
                          <small className="form-text text-muted">
                            <i className="fas fa-spinner fa-spin"></i> Loading departments...
                          </small>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="reportingManager" className="form-label">
                          Reporting Manager
                        </label>
                        <select
                          id="reportingManager"
                          name="reportingManager"
                          className="form-control"
                          value={formData.reportingManager}
                          onChange={handleChange}
                          disabled={loadingReportingManagers}
                        >
                          <option value="">
                            {loadingReportingManagers ? 'Loading reporting managers...' : 'Select Reporting Manager'}
                          </option>
                          {reportingManagers.map((manager) => (
                            <option key={manager.id} value={manager.id}>
                              {manager.name}
                            </option>
                          ))}
                        </select>
                        {loadingReportingManagers && (
                          <small className="form-text text-muted">
                            <i className="fas fa-spinner fa-spin"></i> Loading reporting managers...
                          </small>
                        )}
                      </div>

                      {/* Column 2 */}
                      <div className="form-group">
                        <label htmlFor="pfUanNumber" className="form-label">
                          PF UAN Number
                        </label>
                        <input
                          type="text"
                          id="pfUanNumber"
                          name="pfUanNumber"
                          className="form-control"
                          value={formData.pfUanNumber}
                          onChange={handleChange}
                          placeholder="Enter PF UAN Number"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="pfNumber" className="form-label">
                          PF Number
                        </label>
                        <input
                          type="text"
                          id="pfNumber"
                          name="pfNumber"
                          className="form-control"
                          value={formData.pfNumber}
                          onChange={handleChange}
                          placeholder="Enter PF Number"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="esiNumber" className="form-label">
                          ESI Number
                        </label>
                        <input
                          type="text"
                          id="esiNumber"
                          name="esiNumber"
                          className="form-control"
                          value={formData.esiNumber}
                          onChange={handleChange}
                          placeholder="Enter ESI Number"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="dateOfJoining" className="form-label">
                          Date Of Joining<span className="required-star">*</span>
                        </label>
                        <div className="date-input-wrapper">
                          <input
                            type="date"
                            id="dateOfJoining"
                            name="dateOfJoining"
                            className={`form-control ${errors.dateOfJoining ? 'is-invalid' : ''}`}
                            value={formData.dateOfJoining}
                            onChange={handleChange}
                          />
                          <i className="fas fa-calendar-alt date-icon"></i>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="employmentStatus" className="form-label">
                          Employee Status<span className="required-star">*</span>
                          {/* <i className="fas fa-info-circle ml-1" style={{ fontSize: '12px', color: '#0094a9' }}></i> */}
                        </label>
                        <select
                          id="employmentStatus"
                          name="employmentStatus"
                          className={`form-control ${errors.employmentStatus ? 'is-invalid' : ''}`}
                          value={formData.employmentStatus}
                          onChange={handleChange}
                        >
                          <option value="">Select Employee Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="terminated">Terminated</option>
                          <option value="resigned">Resigned</option>
                        </select>
                      </div>
                    </div>

                    {/* Checkboxes Section */}
                    <div className="form-group form-group-spacing">
                      <label className="form-label form-label-spacing">
                        Applicable Options
                      </label>
                      <div className="checkbox-group">
                        <div className="form-check form-check-no-margin">
                          <input
                            type="checkbox"
                            id="pfApplicable"
                            name="pfApplicable"
                            className="form-check-input"
                            checked={formData.pfApplicable}
                            onChange={(e) => setFormData(prev => ({ ...prev, pfApplicable: e.target.checked }))}
                          />
                          <label className="form-check-label" htmlFor="pfApplicable">
                            PF Applicable
                          </label>
                        </div>

                        <div className="form-check form-check-no-margin">
                          <input
                            type="checkbox"
                            id="tdsApplicable"
                            name="tdsApplicable"
                            className="form-check-input"
                            checked={formData.tdsApplicable}
                            onChange={(e) => setFormData(prev => ({ ...prev, tdsApplicable: e.target.checked }))}
                          />
                          <label className="form-check-label" htmlFor="tdsApplicable">
                            TDS Applicable
                          </label>
                        </div>

                        <div className="form-check form-check-no-margin">
                          <input
                            type="checkbox"
                            id="remoteWorkAllowed"
                            name="remoteWorkAllowed"
                            className="form-check-input"
                            checked={formData.remoteWorkAllowed}
                            onChange={(e) => setFormData(prev => ({ ...prev, remoteWorkAllowed: e.target.checked }))}
                          />
                          <label className="form-check-label" htmlFor="remoteWorkAllowed">
                            Remote Work Allowed
                          </label>
                        </div>

                        <div className="form-check form-check-no-margin">
                          <input
                            type="checkbox"
                            id="checkInCheckOutNotRequired"
                            name="checkInCheckOutNotRequired"
                            className="form-check-input"
                            checked={formData.checkInCheckOutNotRequired}
                            onChange={(e) => setFormData(prev => ({ ...prev, checkInCheckOutNotRequired: e.target.checked }))}
                          />
                          <label className="form-check-label" htmlFor="checkInCheckOutNotRequired">
                            Check-in/Check-out Not Required
                            <span className="info-tooltip-wrapper info-icon-wrapper">
                              <i className="fas fa-info-circle info-icon"></i>
                              <span className="info-tooltip">
                                This option is for higher-level employees who are not required to follow time sheet or leave tracking. Payroll will be generated base on total working days with full salary.
                              </span>
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setActiveTab('basic')}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          if (validateEmployment()) {
                            toast.success('Employment details saved successfully!');
                            // Mark experience tab as accessible
                            setValidatedTabs(prev => {
                              if (!prev.includes('experience')) {
                                return [...prev, 'experience'];
                              }
                              return prev;
                            });
                            setActiveTab('experience');
                          } else {
                            toast.error('Please fill all required fields');
                          }
                        }}
                      >
                        Save and Next
                      </button>
                      {/* Show Approved button only when status is WAITING FOR APPROVAL */}
                      {(originalStatus?.toUpperCase() === 'WAITING FOR APPROVAL' || originalStatus?.toLowerCase() === 'waiting for approval') && (
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={handleApprove}
                          disabled={loading}
                          style={{
                            backgroundColor: '#10b981',
                            borderColor: '#10b981',
                            marginLeft: '10px'
                          }}
                        >
                          {loading ? 'Approving...' : 'Approved'}
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              )}

              {/* Experience Tab */}
              {activeTab === 'experience' && (
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <h4 className="section-title">
                      Previous Company Details
                      {/* <i className="fas fa-info-circle ml-2" style={{ fontSize: '14px', color: '#6b7280' }}></i> */}
                    </h4>
                    <p className="section-subtitle">
                      Update employee information and details.
                    </p>

                    {/* Experience List */}
                    {formData.experiences.map((exp, index) => (
                      <div key={exp.id} className="experience-item experience-item-spacing">
                        <div className="form-row-3">
                          <div className="form-group">
                            <label className="form-label">Company Name</label>
                            <input
                              type="text"
                              className="form-control"
                              value={exp.companyName}
                              onChange={(e) => handleExperienceChange(index, 'companyName', e.target.value)}
                              placeholder="Enter company name"
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Designation</label>
                            <input
                              type="text"
                              className="form-control"
                              value={exp.designation}
                              onChange={(e) => handleExperienceChange(index, 'designation', e.target.value)}
                              placeholder="Enter designation"
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Period From</label>
                            <div className="date-input-wrapper">
                              <input
                                type="date"
                                className="form-control"
                                value={exp.periodFrom}
                                onChange={(e) => {
                                  handleExperienceChange(index, 'periodFrom', e.target.value);
                                }}
                              />
                              <i className="fas fa-calendar-alt date-icon"></i>
                            </div>
                          </div>

                          <div className="form-group">
                            <label className="form-label">Period To</label>
                            {index === 0 ? (
                              <div className="period-to-container">
                                <div className="date-input-wrapper date-wrapper-flex">
                                  <input
                                    type="date"
                                    className={`form-control ${exp.isPresent ? 'date-input-disabled' : 'date-input-enabled'}`}
                                    value={exp.periodTo}
                                    onChange={(e) => {
                                      handleExperienceChange(index, 'periodTo', e.target.value);
                                      handleExperienceChange(index, 'isPresent', false);
                                    }}
                                    disabled={exp.isPresent}
                                  />
                                  <i className="fas fa-calendar-alt date-icon"></i>
                                </div>

                              </div>
                            ) : (
                              <div className="date-input-wrapper">
                                <input
                                  type="date"
                                  className="form-control"
                                  value={exp.periodTo}
                                  onChange={(e) => {
                                    handleExperienceChange(index, 'periodTo', e.target.value);
                                    handleExperienceChange(index, 'isPresent', false);
                                  }}
                                  required
                                />
                                <i className="fas fa-calendar-alt date-icon"></i>
                              </div>
                            )}
                          </div>

                          <div className="form-group">
                            <label className="form-label">Experience Duration</label>
                            <input
                              type="text"
                              className="form-control"
                              value={exp.duration}
                              readOnly
                              placeholder="Auto calculated"
                            />
                          </div>

                          <div className="form-group form-group-flex">
                            <button
                              type="button"
                              onClick={() => handleRemoveExperience(exp.id)}
                              className="btn-remove"
                            >
                              <i className="fas fa-trash-alt"></i> Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add Experience Button */}
                    <div className="add-item-container">
                      <button
                        type="button"
                        onClick={handleAddExperience}
                        className="btn-add-item"
                      >
                        <i className="fas fa-plus"></i> Add Experience
                      </button>
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setActiveTab('employment')}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          if (formData.experiences.length === 0) {
                            toast.error('Please add at least one experience record');
                            return;
                          }
                          toast.success('Experience details saved successfully!');
                          // Mark education tab as accessible
                          setValidatedTabs(prev => {
                            if (!prev.includes('education')) {
                              return [...prev, 'education'];
                            }
                            return prev;
                          });
                          setActiveTab('education');
                        }}
                      >
                        Save and Next
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* Education Tab */}
              {activeTab === 'education' && (
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <h4 className="section-title">Education Details</h4>
                    <p className="section-subtitle">
                      Add employee's educational qualifications
                    </p>

                    {/* Education List */}
                    {formData.educationDetails.map((edu, index) => (
                      <div key={edu.id} className="experience-item experience-item-spacing">
                        <div className="form-row-3">
                          <div className="form-group">
                            <label className="form-label">
                              Qualification<span className="required-star">*</span>
                            </label>
                            <select
                              className="form-control"
                              value={edu.qualification}
                              onChange={(e) => handleEducationChange(index, 'qualification', e.target.value)}
                            >
                              <option value="">Select Qualification</option>
                              <option value="10th">10th</option>
                              <option value="12th">12th</option>
                              <option value="Diploma">Diploma</option>
                              <option value="Bachelor's Degree">Bachelor's Degree</option>
                              <option value="Master's Degree">Master's Degree</option>
                              <option value="PhD">PhD</option>
                              <option value="Others">Others</option>
                            </select>
                          </div>

                          <div className="form-group">
                            <label className="form-label">
                              Board/University<span className="required-star">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={edu.boardUniversity}
                              onChange={(e) => handleEducationChange(index, 'boardUniversity', e.target.value)}
                              placeholder="Enter board/university name"
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">
                              Year of Passing<span className="required-star">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={edu.yearOfPassing}
                              onChange={(e) => handleEducationChange(index, 'yearOfPassing', e.target.value)}
                              placeholder="YYYY"
                              maxLength="4"
                            />
                          </div>

                          {/* Name of the Degree - Show for Diploma, Bachelor's, Master's, PhD (not for 10th and 12th) */}
                          {edu.qualification !== '10th' && edu.qualification !== '12th' && (
                            <div className="form-group">
                              <label className="form-label">Name of the Degree</label>
                              <input
                                type="text"
                                className="form-control"
                                value={edu.nameOfDegree}
                                onChange={(e) => handleEducationChange(index, 'nameOfDegree', e.target.value)}
                                placeholder="Enter degree name (e.g., B.Tech, M.Sc)"
                              />
                            </div>
                          )}

                          {/* Field of Study - Show for 12th and above (not for 10th) */}
                          {edu.qualification !== '10th' && (
                            <div className="form-group">
                              <label className="form-label">Field of Study</label>
                              <input
                                type="text"
                                className="form-control"
                                value={edu.fieldOfStudy}
                                onChange={(e) => handleEducationChange(index, 'fieldOfStudy', e.target.value)}
                                placeholder="Enter field of study (e.g., Computer Science)"
                              />
                            </div>
                          )}

                          <div className="form-group">
                            <label className="form-label">
                              Percentage/CGPA<span className="required-star">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={edu.percentage}
                              onChange={(e) => handleEducationChange(index, 'percentage', e.target.value)}
                              placeholder="Enter percentage or CGPA"
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">
                              Relevant Certificate No<span className="required-star">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={edu.relevantCertificateNo}
                              onChange={(e) => handleEducationChange(index, 'relevantCertificateNo', e.target.value)}
                              placeholder="Enter certificate number"
                            />
                          </div>

                          <div className="form-group form-group-flex">
                            <button
                              type="button"
                              onClick={() => handleRemoveEducation(edu.id)}
                              className="btn-remove"
                            >
                              <i className="fas fa-trash-alt"></i> Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add Education Button */}
                    <div className="add-item-container">
                      <button
                        type="button"
                        onClick={handleAddEducation}
                        className="btn-add-item"
                      >
                        <i className="fas fa-plus"></i> Add Education
                      </button>
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setActiveTab('experience')}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          if (validateEducation()) {
                            toast.success('Education details saved successfully!');
                            // Mark bank tab as accessible
                            setValidatedTabs(prev => {
                              if (!prev.includes('bank')) {
                                return [...prev, 'bank'];
                              }
                              return prev;
                            });
                            setActiveTab('bank');
                          }
                        }}
                      >
                        Save and Next
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* Bank Tab */}
              {activeTab === 'bank' && (
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <h4 className="section-title">Bank Details</h4>
                    <p className="section-subtitle">
                      Add employee's bank account information
                    </p>

                    <div className="form-row-3">
                      <div className="form-group">
                        <label htmlFor="bankName" className="form-label">
                          Bank Name<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="bankName"
                          name="bankName"
                          className={`form-control ${errors.bankName ? 'is-invalid' : ''}`}
                          value={formData.bankName}
                          onChange={handleChange}
                          placeholder="Enter bank name"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="accountNumber" className="form-label">
                          Account Number<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="accountNumber"
                          name="accountNumber"
                          className={`form-control ${errors.accountNumber ? 'is-invalid' : ''}`}
                          value={formData.accountNumber}
                          onChange={handleChange}
                          placeholder="Enter account number"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="ifscCode" className="form-label">
                          IFSC Code<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="ifscCode"
                          name="ifscCode"
                          className={`form-control text-uppercase ${errors.ifscCode ? 'is-invalid' : ''}`}
                          value={formData.ifscCode}
                          onChange={handleChange}
                          placeholder="Enter IFSC code"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="branchName" className="form-label">
                          Branch Name
                        </label>
                        <input
                          type="text"
                          id="branchName"
                          name="branchName"
                          className="form-control"
                          value={formData.branchName}
                          onChange={handleChange}
                          placeholder="Enter branch name"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="accountHolderName" className="form-label">
                          Account Holder Name<span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="accountHolderName"
                          name="accountHolderName"
                          className={`form-control ${errors.accountHolderName ? 'is-invalid' : ''}`}
                          value={formData.accountHolderName}
                          onChange={handleChange}
                          placeholder="Enter account holder name"
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setActiveTab('education')}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          if (validateBank()) {
                            toast.success('Bank details saved successfully!');
                            // Mark document tab as accessible
                            setValidatedTabs(prev => {
                              if (!prev.includes('document')) {
                                return [...prev, 'document'];
                              }
                              return prev;
                            });
                            setActiveTab('document');
                          } else {
                            toast.error('Please fill all required fields');
                          }
                        }}
                      >
                        Save and Next
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* Document Tab */}
              {activeTab === 'document' && (
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <h4 className="section-title">Document Details</h4>
                    <p className="section-subtitle">
                      Upload employee's required documents
                    </p>

                    <div className="form-row-3">
                      <div className="form-group">
                        <label htmlFor="aadharCard" className="form-label">
                          Aadhar Card<span className="required-star">*</span>
                        </label>
                        <div className="file-input-wrapper">
                          {/* Display existing document if URL or object with previewUrl */}
                          {formData.aadharCard && (
                            <div className="mb-3" style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '25px', minHeight: '260px', backgroundColor: '#f9fafb' }}>
                              <div
                                className="education-certificate-item"
                                onClick={() => {
                                  const url = typeof formData.aadharCard === 'string'
                                    ? formData.aadharCard
                                    : (formData.aadharCard?.previewUrl || formData.aadharCard?.s3Key);
                                  if (url) window.open(url, '_blank');
                                }}
                                style={{ cursor: 'pointer', margin: '0 auto', maxWidth: '280px' }}
                              >
                                {(() => {
                                  const previewUrl = typeof formData.aadharCard === 'string'
                                    ? formData.aadharCard
                                    : formData.aadharCard?.previewUrl;
                                  if (!previewUrl) return null;

                                  // Check if it's an image using contentType (more reliable) or file extension
                                  const contentType = typeof formData.aadharCard === 'object' ? formData.aadharCard?.contentType : null;
                                  const urlWithoutParams = previewUrl.split('?')[0].toLowerCase();
                                  const isImageByExtension = urlWithoutParams.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/);
                                  const isImageByContentType = contentType && contentType.startsWith('image/');
                                  const isBlobUrl = previewUrl.startsWith('blob:');
                                  const isImage = isImageByContentType || isImageByExtension || isBlobUrl;

                                  return isImage ? (
                                    <img
                                      src={previewUrl}
                                      alt="Aadhar Card"
                                      className="education-certificate-image"
                                      onError={(e) => {
                                        // If image fails to load, show PDF icon
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = '<div style="text-align: center;"><div style="font-size: 48px; color: #dc2626;"><i class="fas fa-file-pdf"></i></div></div>';
                                      }}
                                    />
                                  ) : (
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '48px', color: '#dc2626' }}>
                                        <i className="fas fa-file-pdf"></i>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          )}
                          <input
                            type="file"
                            id="aadharCard"
                            name="aadharCard"
                            className="form-control file-input-padding"
                            accept="image/*,.pdf"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                try {
                                  // Upload file immediately and store both S3 key and preview URL
                                  const uploadResult = await uploadFile(file, 'aadharCard');
                                  setFormData(prev => ({ ...prev, aadharCard: uploadResult }));
                                  toast.success('Aadhar Card uploaded successfully');
                                } catch (error) {
                                  // Reset file input on error
                                  e.target.value = '';
                                  const previousValue = formData.aadharCard && typeof formData.aadharCard === 'string' ? formData.aadharCard : null;
                                  setFormData(prev => ({ ...prev, aadharCard: previousValue }));
                                }
                              }
                            }}
                          />
                          {formData.aadharCard && (
                            <span className="file-success-text">
                              <i className="fas fa-check-circle"></i> Aadhar Card {typeof formData.aadharCard === 'string' ? 'uploaded' : 'uploaded'}
                              {uploadingFiles.aadharCard && (
                                <span className="ml-2">
                                  <i className="fas fa-spinner fa-spin"></i> Uploading...
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="panCard" className="form-label">
                          PAN Card<span className="required-star">*</span>
                        </label>
                        <div className="file-input-wrapper">
                          {/* Display existing document if URL or object with previewUrl */}
                          {formData.panCard && (
                            <div className="mb-3" style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '25px', minHeight: '260px', backgroundColor: '#f9fafb' }}>
                              <div
                                className="education-certificate-item"
                                onClick={() => {
                                  const url = typeof formData.panCard === 'string'
                                    ? formData.panCard
                                    : (formData.panCard?.previewUrl || formData.panCard?.s3Key);
                                  if (url) window.open(url, '_blank');
                                }}
                                style={{ cursor: 'pointer', margin: '0 auto', maxWidth: '280px' }}
                              >
                                {(() => {
                                  const previewUrl = typeof formData.panCard === 'string'
                                    ? formData.panCard
                                    : formData.panCard?.previewUrl;
                                  if (!previewUrl) return null;

                                  // Check if it's an image using contentType (more reliable) or file extension
                                  const contentType = typeof formData.panCard === 'object' ? formData.panCard?.contentType : null;
                                  const urlWithoutParams = previewUrl.split('?')[0].toLowerCase();
                                  const isImageByExtension = urlWithoutParams.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/);
                                  const isImageByContentType = contentType && contentType.startsWith('image/');
                                  const isBlobUrl = previewUrl.startsWith('blob:');
                                  const isImage = isImageByContentType || isImageByExtension || isBlobUrl;

                                  return isImage ? (
                                    <img
                                      src={previewUrl}
                                      alt="PAN Card"
                                      className="education-certificate-image"
                                      onError={(e) => {
                                        // If image fails to load, show PDF icon
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = '<div style="text-align: center;"><div style="font-size: 48px; color: #dc2626;"><i class="fas fa-file-pdf"></i></div></div>';
                                      }}
                                    />
                                  ) : (
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '48px', color: '#dc2626' }}>
                                        <i className="fas fa-file-pdf"></i>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          )}
                          <input
                            type="file"
                            id="panCard"
                            name="panCard"
                            className="form-control file-input-padding"
                            accept="image/*,.pdf"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                try {
                                  // Upload file immediately and store both S3 key and preview URL
                                  const uploadResult = await uploadFile(file, 'panCard');
                                  setFormData(prev => ({ ...prev, panCard: uploadResult }));
                                  toast.success('PAN Card uploaded successfully');
                                } catch (error) {
                                  // Reset file input on error
                                  e.target.value = '';
                                  const previousValue = formData.panCard && typeof formData.panCard === 'string' ? formData.panCard : null;
                                  setFormData(prev => ({ ...prev, panCard: previousValue }));
                                }
                              }
                            }}
                          />
                          {formData.panCard && (
                            <span className="file-success-text">
                              <i className="fas fa-check-circle"></i> PAN Card {typeof formData.panCard === 'string' ? 'uploaded' : 'uploaded'}
                              {uploadingFiles.panCard && (
                                <span className="ml-2">
                                  <i className="fas fa-spinner fa-spin"></i> Uploading...
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Education Certificates
                        </label>
                        <div className="file-input-wrapper">
                          {/* Display existing documents if URLs or objects with previewUrl */}
                          {formData.educationCertificates && formData.educationCertificates.length > 0 && formData.educationCertificates.some(cert => cert && (typeof cert === 'string' || (typeof cert === 'object' && cert.previewUrl))) && (
                            <div className="mb-3" style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '7px', minHeight: '260px', backgroundColor: '#f9fafb' }}>
                              <div className="education-certificates-grid-container">
                                {formData.educationCertificates.map((cert, index) => {
                                  if (!cert) return null;

                                  const previewUrl = typeof cert === 'string' ? cert : cert.previewUrl;
                                  if (!previewUrl) return null;

                                  // Check if it's an image using contentType (more reliable) or file extension
                                  const contentType = typeof cert === 'object' ? cert?.contentType : null;
                                  const urlWithoutParams = previewUrl.split('?')[0].toLowerCase();
                                  const isImageByExtension = urlWithoutParams.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/);
                                  const isImageByContentType = contentType && contentType.startsWith('image/');
                                  const isBlobUrl = previewUrl.startsWith('blob:');
                                  const isImage = isImageByContentType || isImageByExtension || isBlobUrl;

                                  return (
                                    <div
                                      key={`cert-${index}`}
                                      style={{ position: 'relative' }}
                                    >
                                      {/* Certificate card container */}
                                      <div
                                        className="education-certificate-item"
                                        style={{ position: 'relative', cursor: 'pointer' }}
                                      >
                                        {/* Remove button - positioned at corner edge */}
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setFormData(prev => ({
                                              ...prev,
                                              educationCertificates: prev.educationCertificates.filter((_, i) => i !== index)
                                            }));
                                            toast.success('Education certificate removed');
                                          }}
                                          className="btn-remove-certificate"
                                          style={{
                                            position: 'absolute',
                                            top: '-10px',
                                            right: '-10px',
                                            background: '#dc2626',
                                            color: 'white',
                                            border: '2px solid white',
                                            borderRadius: '50%',
                                            width: '24px',
                                            height: '24px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            zIndex: 10,
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                            padding: 0
                                          }}
                                          title="Remove certificate"
                                        >
                                          <i className="fas fa-times" style={{ fontSize: '11px' }}></i>
                                        </button>

                                        {/* Certificate preview - clickable to view */}
                                        <div
                                          onClick={() => {
                                            const url = typeof cert === 'string' ? cert : (cert.previewUrl || cert.s3Key);
                                            if (url && !url.startsWith('blob:')) {
                                              window.open(url, '_blank');
                                            }
                                          }}
                                          style={{ width: '100%', height: '100%' }}
                                        >
                                          {isImage ? (
                                            <img
                                              src={previewUrl}
                                              alt={`Education Certificate ${index + 1}`}
                                              className="education-certificate-image"
                                              onError={(e) => {
                                                // If image fails to load, show PDF icon
                                                e.target.style.display = 'none';
                                                e.target.parentElement.innerHTML = '<div style="text-align: center;"><div style="font-size: 48px; color: #dc2626;"><i class="fas fa-file-pdf"></i></div></div>';
                                              }}
                                            />
                                          ) : (
                                            <div style={{ textAlign: 'center' }}>
                                              <div style={{ fontSize: '48px', color: '#dc2626' }}>
                                                <i className="fas fa-file-pdf"></i>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Replace button - outside container, below the card */}
                                      <div style={{ marginTop: '8px', textAlign: 'center' }}>
                                        <label
                                          htmlFor={`replace-cert-${index}`}
                                          style={{
                                            background: '#2563eb',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '6px 12px',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            transition: 'all 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.background = '#1d4ed8';
                                            e.target.style.transform = 'translateY(-1px)';
                                            e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.15)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.background = '#2563eb';
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                          }}
                                          title="Replace certificate"
                                        >
                                          <i className="fas fa-exchange-alt" style={{ fontSize: '11px' }}></i>
                                          Replace
                                        </label>
                                        <input
                                          type="file"
                                          id={`replace-cert-${index}`}
                                          style={{ display: 'none' }}
                                          accept="image/*,.pdf"
                                          onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                              try {
                                                // Upload new file
                                                const uploadResult = await uploadFile(file, 'educationCertificate', index);

                                                // Replace the certificate at this index
                                                setFormData(prev => {
                                                  const updated = [...prev.educationCertificates];
                                                  updated[index] = uploadResult;
                                                  return {
                                                    ...prev,
                                                    educationCertificates: updated
                                                  };
                                                });

                                                toast.success('Education certificate replaced successfully');
                                              } catch (error) {
                                                e.target.value = '';
                                                toast.error('Failed to replace education certificate');
                                              }
                                            }
                                          }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          <input
                            type="file"
                            className="form-control file-input-padding"
                            accept="image/*,.pdf"
                            multiple
                            onChange={async (e) => {
                              const files = Array.from(e.target.files);
                              if (files.length > 0) {
                                try {
                                  const currentCount = formData.educationCertificates.filter(cert => cert && (typeof cert === 'string' || (typeof cert === 'object' && cert.s3Key))).length;
                                  // Upload all files and store objects with S3 keys and preview URLs
                                  const uploadPromises = files.map((file, index) =>
                                    uploadFile(file, 'educationCertificate', currentCount + index)
                                  );
                                  const uploadResults = await Promise.all(uploadPromises);

                                  setFormData(prev => ({
                                    ...prev,
                                    educationCertificates: [...prev.educationCertificates, ...uploadResults]
                                  }));

                                  toast.success(`${files.length} education certificate(s) uploaded successfully`);
                                } catch (error) {
                                  // Reset file input on error
                                  e.target.value = '';
                                  toast.error('Failed to upload some education certificates');
                                }
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setActiveTab('bank')}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          if (validateDocument()) {
                            // Final submission - handleSubmit will show success message
                            handleSubmit(e);
                          }
                        }}
                      >
                        Save Employee
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* Other Tabs Placeholder */}
              {activeTab !== 'basic' && activeTab !== 'employment' && activeTab !== 'experience' && activeTab !== 'education' && activeTab !== 'bank' && activeTab !== 'document' && (
                <div className="empty-state-container">
                  <i className="fas fa-info-circle empty-state-icon"></i>
                  <p>{tabs.find(t => t.id === activeTab)?.label} section coming soon...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Address Modal */}
      {showAddressModal && (
        <div className="modal-overlay" onClick={handleCloseAddressModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">Employee Address</h5>
              <button
                type="button"
                className="modal-close"
                onClick={handleCloseAddressModal}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSaveAddress}>
                {/* Address Type - Full Width */}
                <div
                  className={`form-group modal-address-type-group ${showAddressTypeDropdown ? 'active' : ''} ${addressData.sameAsPermanent ? 'disabled' : ''}`}
                  data-dropdown-active={showAddressTypeDropdown}
                >
                  <label htmlFor="addressType" className="form-label">
                    Address Type
                    {addressData.sameAsPermanent && (
                      <span className="modal-address-type-hint">
                        (Optional - will create both addresses)
                      </span>
                    )}
                  </label>
                  <div className={`custom-dropdown-wrapper ${showAddressTypeDropdown ? 'active' : ''}`} ref={addressTypeDropdownRef}>
                    <div
                      className={`custom-dropdown ${addressErrors.addressType ? 'is-invalid' : ''} ${showAddressTypeDropdown ? 'active' : ''} ${addressData.sameAsPermanent ? 'disabled' : ''} ${addressData.sameAsPermanent ? 'custom-dropdown-disabled' : 'custom-dropdown-enabled'}`}
                      onClick={() => {
                        if (!addressData.sameAsPermanent && !showAddressTypeDropdown) {
                          setShowAddressTypeDropdown(true);
                          setAddressTypeSearchTerm('');
                        }
                      }}
                      tabIndex={addressData.sameAsPermanent ? -1 : 0}
                      onKeyDown={showAddressTypeDropdown ? undefined : handleAddressTypeDisplayKeyDown}
                    >
                      {showAddressTypeDropdown ? (
                        <input
                          type="text"
                          className="custom-dropdown-input"
                          value={addressTypeSearchTerm}
                          onChange={(e) => {
                            setAddressTypeSearchTerm(e.target.value);
                          }}
                          onKeyDown={handleAddressTypeKeyDown}
                          placeholder="Search address type..."
                          autoFocus
                        />
                      ) : (
                        <div className="custom-dropdown-display" tabIndex={-1}>
                          {addressData.addressType ? addressTypes.find(t => t.toLowerCase().replace(' address', '') === addressData.addressType) || 'Select Address Type' : 'Select Address Type'}
                        </div>
                      )}
                      <i
                        className={`fas fa-chevron-${showAddressTypeDropdown ? 'up' : 'down'} custom-dropdown-arrow`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAddressTypeDropdown(!showAddressTypeDropdown);
                          if (!showAddressTypeDropdown) {
                            setAddressTypeSearchTerm('');
                          }
                        }}
                      ></i>
                    </div>
                    {showAddressTypeDropdown && (
                      <div className="custom-dropdown-list">
                        <div className="custom-dropdown-options">
                          {filteredAddressTypes.length > 0 ? (
                            filteredAddressTypes.map((type) => (
                              <div
                                key={type}
                                className={`custom-dropdown-option ${addressData.addressType === type.toLowerCase().replace(' address', '') ? 'selected' : ''}`}
                                onClick={() => handleAddressTypeSelect(type)}
                              >
                                {type}
                              </div>
                            ))
                          ) : (
                            <div className="custom-dropdown-option no-results">No address types found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {addressErrors.addressType && (
                    <div className="invalid-feedback">{addressErrors.addressType}</div>
                  )}
                </div>

                {/* Address 1 and Address 2 - Side by Side */}
                <div className={`row ${showAddressTypeDropdown ? 'modal-row-z-index-1' : 'modal-row-z-index-auto'}`}>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="address1" className="form-label">
                        Address 1<span className="required-star">*</span>
                      </label>
                      <input
                        type="text"
                        id="address1"
                        name="address1"
                        className={`form-control ${addressErrors.address1 ? 'is-invalid' : ''}`}
                        value={addressData.address1}
                        onChange={handleAddressChange}
                        placeholder="Enter address line 1"
                      />
                      {addressErrors.address1 && (
                        <div className="invalid-feedback">{addressErrors.address1}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="address2" className="form-label">Address 2</label>
                      <input
                        type="text"
                        id="address2"
                        name="address2"
                        className="form-control"
                        value={addressData.address2}
                        onChange={handleAddressChange}
                        placeholder="Enter address line 2"
                      />
                    </div>
                  </div>
                </div>

                {/* Country and State - Side by Side */}
                <div className="row">
                  <div className={`col-md-6 ${showCountryDropdown ? 'modal-col-z-index-9999' : (showStateDropdown ? 'modal-col-z-index-1' : 'modal-col-z-index-auto')}`}>
                    <div className="form-group">
                      <label htmlFor="country" className="form-label">
                        Country<span className="required-star">*</span>
                      </label>
                      <div className={`custom-dropdown-wrapper ${showCountryDropdown ? 'active' : ''}`} ref={countryDropdownRef}>
                        <div
                          className={`custom-dropdown ${addressErrors.country ? 'is-invalid' : ''} ${showCountryDropdown ? 'active' : ''}`}
                          onClick={() => {
                            if (!showCountryDropdown) {
                              setShowCountryDropdown(true);
                              setCountrySearchTerm('');
                            }
                          }}
                          tabIndex={0}
                          onKeyDown={showCountryDropdown ? undefined : handleCountryDisplayKeyDown}
                        >
                          {showCountryDropdown ? (
                            <input
                              type="text"
                              className="custom-dropdown-input"
                              value={countrySearchTerm}
                              onChange={(e) => {
                                setCountrySearchTerm(e.target.value);
                              }}
                              onKeyDown={handleCountryKeyDown}
                              placeholder="Search country..."
                              autoFocus
                            />
                          ) : (
                            <div className="custom-dropdown-display" tabIndex={-1}>
                              {addressData.country || 'Select Country'}
                            </div>
                          )}
                          <i
                            className={`fas fa-chevron-${showCountryDropdown ? 'up' : 'down'} custom-dropdown-arrow`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowCountryDropdown(!showCountryDropdown);
                              if (!showCountryDropdown) {
                                setCountrySearchTerm('');
                              }
                            }}
                          ></i>
                        </div>
                        {showCountryDropdown && (
                          <div className="custom-dropdown-list">
                            <div className="custom-dropdown-options">
                              {filteredCountries.length > 0 ? (
                                filteredCountries.map((country) => (
                                  <div
                                    key={country}
                                    className={`custom-dropdown-option ${addressData.country === country ? 'selected' : ''}`}
                                    onClick={() => handleCountrySelect(country)}
                                  >
                                    {country}
                                  </div>
                                ))
                              ) : (
                                <div className="custom-dropdown-option no-results">No countries found</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {addressErrors.country && (
                        <div className="invalid-feedback">{addressErrors.country}</div>
                      )}
                    </div>
                  </div>
                  <div className={`col-md-6 ${showStateDropdown ? 'modal-col-z-index-9999' : 'modal-col-z-index-auto'}`}>
                    <div className="form-group">
                      <label htmlFor="state" className="form-label">
                        State<span className="required-star">*</span>
                      </label>
                      <div className={`custom-dropdown-wrapper ${showStateDropdown ? 'active' : ''}`} ref={stateDropdownRef}>
                        <div
                          className={`custom-dropdown ${addressErrors.state ? 'is-invalid' : ''} ${showStateDropdown ? 'active' : ''}`}
                          onClick={() => {
                            if (!showStateDropdown) {
                              setShowStateDropdown(true);
                              setStateSearchTerm('');
                            }
                          }}
                          tabIndex={0}
                          onKeyDown={showStateDropdown ? undefined : handleStateDisplayKeyDown}
                        >
                          {showStateDropdown ? (
                            <input
                              type="text"
                              className="custom-dropdown-input"
                              value={stateSearchTerm}
                              onChange={(e) => {
                                setStateSearchTerm(e.target.value);
                              }}
                              onKeyDown={handleStateKeyDown}
                              placeholder="Search state..."
                              autoFocus
                            />
                          ) : (
                            <div className="custom-dropdown-display" tabIndex={-1}>
                              {addressData.state || 'Select State'}
                            </div>
                          )}
                          <i
                            className={`fas fa-chevron-${showStateDropdown ? 'up' : 'down'} custom-dropdown-arrow`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowStateDropdown(!showStateDropdown);
                              if (!showStateDropdown) {
                                setStateSearchTerm('');
                              } else {
                                setStateSearchTerm('');
                              }
                            }}
                          ></i>
                        </div>
                        {showStateDropdown && (
                          <div className="custom-dropdown-list">
                            <div className="custom-dropdown-options">
                              {filteredStates.length > 0 ? (
                                filteredStates.map((state) => (
                                  <div
                                    key={state}
                                    className={`custom-dropdown-option ${addressData.state === state ? 'selected' : ''}`}
                                    onClick={() => handleStateSelect(state)}
                                  >
                                    {state}
                                  </div>
                                ))
                              ) : (
                                <div className="custom-dropdown-option no-results">No states found</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {addressErrors.state && (
                        <div className="invalid-feedback">{addressErrors.state}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* City and Pincode - Side by Side */}
                <div className="row modal-city-pincode-row">
                  <div className={`col-md-6 ${showStateDropdown ? 'modal-col-z-index-1' : 'modal-col-z-index-auto'}`}>
                    <div className="form-group">
                      <label htmlFor="city" className="form-label">
                        City<span className="required-star">*</span>
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        className={`form-control ${addressErrors.city ? 'is-invalid' : ''}`}
                        value={addressData.city}
                        onChange={handleAddressChange}
                        placeholder="Enter city"
                      />
                      {addressErrors.city && (
                        <div className="invalid-feedback">{addressErrors.city}</div>
                      )}
                    </div>
                  </div>
                  <div className={`col-md-6 ${showStateDropdown ? 'modal-col-z-index-1' : 'modal-col-z-index-auto'}`}>
                    <div className="form-group">
                      <label htmlFor="pincode" className="form-label">
                        Pincode<span className="required-star">*</span>
                      </label>
                      <input
                        type="text"
                        id="pincode"
                        name="pincode"
                        className={`form-control ${addressErrors.pincode ? 'is-invalid' : ''}`}
                        value={addressData.pincode}
                        onChange={handlePincodeChange}
                        placeholder="Enter 6-digit pincode"
                        maxLength="6"
                        inputMode="numeric"
                      />
                      {addressErrors.pincode && (
                        <div className="invalid-feedback">{addressErrors.pincode}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group mt-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="sameAsPermanent"
                      name="sameAsPermanent"
                      className="form-check-input"
                      checked={addressData.sameAsPermanent}
                      onChange={handleAddressChange}
                    />
                    <label className="form-check-label" htmlFor="sameAsPermanent">
                      Present address same as Permanent address
                    </label>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCloseAddressModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAddressButton}
                    className="btn-primary"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeEdit;
